// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The Ressourcer inspector's field rows and Tabel › Typer's columns as data
 * (v8 prototype "Field rows"): which rows a field view shows, under which
 * headings, and how a type-level row reads and writes a resource key. Pure:
 * no React, no DOM.
 *
 * A field view (`bygning/fieldView`, the inspector's and Tabel's picker) is a
 * template or Alle felter. A template decides the rows and their order — its
 * `resolved_keys`, as the Skabeloner page arranges them — with a heading for
 * every run of keys from one `ResourceKey.category`, so a template that goes
 * back to a category later gets that heading again rather than being
 * reordered. Alle felter is every catalogue key, a category's keys together
 * in the order the catalogue first names the category, each row with its
 * nedrivningsplan reference (BEK 496 bilag) where known.
 *
 * A key gets a *role* (by `sys:` id, else by its label) so the inspector can
 * draw the rows that are more than a value — Mængde with its provenance,
 * Opbygning with its picker, EAK with the build-up suggestion, Problematiske
 * stoffer from the linked samples. Keys with no special role are `generic`
 * and get the editor their `data_type` asks for. The rows the report needs
 * (`REPORT_ROLES`) are in every view even when the template leaves them out:
 * Fordeling på rum after Mængde and Opbygning after EAK when those are shown,
 * the rest under a trailing "Til rapporten" heading.
 *
 * Scope (spec §4.3): the inspector edits the *type*. A type-scoped key is read
 * and written through the type's carrier part (one write changes every
 * part); a part-scoped key is shown as the parts' common value and written to
 * every part of the type.
 */

import type { Resource, ResourceKey, SurveyType, Template } from '../api/types';
import { shownChoice, type PendingChoice } from '../kortlaegning/cellDraft';
import {
  FIXED_COLUMN_KEYS,
  templateColumns,
  typeCarrier,
  type ResourceColumn,
  type ResourceIndex,
} from '../kortlaegning/resources';
import { viewTemplate, type FieldView } from './fieldView';

export type FieldRole =
  | 'materiale'
  | 'potentiale'
  | 'forurenet'
  | 'forureningNote'
  | 'screenet'
  | 'quantity'
  | 'rooms'
  | 'buildup'
  | 'eak'
  | 'tons'
  | 'demonterbar'
  | 'stoffer'
  | 'treatment'
  | 'note'
  | 'stand'
  | 'besluttet'
  | 'generic';

/** Built-ins the inspector shows elsewhere (title, unit suffix, ★ in the header), never as a row. */
export const HEADER_KEYS: ReadonlySet<string> = new Set(['sys:name', 'sys:unit', 'sys:starred']);

const SYS_ROLE: Record<string, FieldRole> = {
  'sys:quantity': 'quantity',
  'sys:room': 'rooms',
  'sys:eak': 'eak',
  'sys:mass_t': 'tons',
  'sys:treatment': 'treatment',
  'sys:environment': 'stoffer',
  'sys:note': 'note',
};

/** Leksikon and user-column labels with a role (lower-case, trimmed). */
const LABEL_ROLE: Record<string, FieldRole> = {
  materialetype: 'materiale',
  materiale: 'materiale',
  potentiale: 'potentiale',
  'potentielt forurenet': 'forurenet',
  'forurening, note': 'forureningNote',
  forureningsnote: 'forureningNote',
  screenet: 'screenet',
  'kan demonteres': 'demonterbar',
  stand: 'stand',
  'besluttet med bygherre': 'besluttet',
  opbygning: 'buildup',
};

/** The role of a key: by `sys:` id, else by label; anything else is `generic`. */
export function roleOf(key: Pick<ResourceKey, 'id' | 'label'>): FieldRole {
  if (key.id.startsWith('sys:')) return SYS_ROLE[key.id] ?? 'generic';
  return LABEL_ROLE[key.label.trim().toLowerCase()] ?? 'generic';
}

/** Row labels for the roles that are not a key's own label. */
const ROLE_LABEL: Partial<Record<FieldRole, string>> = {
  quantity: 'Mængde',
  rooms: 'Fordeling på rum',
  buildup: 'Opbygning',
  eak: 'EAK',
  tons: 'Tons',
  stoffer: 'Problematiske stoffer',
  treatment: 'Behandling',
  note: 'Note',
};

/** Nedrivningsplan references (BEK 496, bilag 2) shown in Alle felter. */
export const LEGAL_REF: Partial<Record<FieldRole, string>> = {
  materiale: '8.d.i · 10.d.i',
  potentiale: '8.d.i',
  forurenet: '8.d.ii',
  forureningNote: '8.d.ii',
  screenet: '8',
  quantity: '10.d.i',
  eak: '10.d.ii',
  tons: '10.d.ii',
  demonterbar: '10.d.ii',
  stoffer: '10.d.iv–v',
  treatment: '10.d.iii',
  besluttet: '21',
};

/** One inspector row. `key` is null for a synthetic row (Fordeling på rum, an Opbygning not created yet, …). */
export interface FieldSpec {
  /** Stable React key: the key id, or `role:<role>`. */
  id: string;
  role: FieldRole;
  key: ResourceKey | null;
  label: string;
  ref: string | null;
}

export interface FieldGroupSpec {
  id: string;
  title: string;
  fields: FieldSpec[];
}

/**
 * The rows every view shows, template or not: what the report and the
 * "Mangler før rapport" checks read (`review`, `checklist`, `submission`).
 */
export const REPORT_ROLES: readonly FieldRole[] = ['quantity', 'rooms', 'buildup', 'eak', 'tons', 'treatment', 'stoffer'];

/** The heading over the report rows a template leaves out. */
const REPORT_GROUP_TITLE = 'Til rapporten';

function spec(role: FieldRole, key: ResourceKey | null, withRef: boolean): FieldSpec {
  // Built-ins and synthetic rows read as the prototype names them; leksikon
  // fields and user columns keep their own label.
  const fixed = !key || key.id.startsWith('sys:') || role === 'stoffer' || role === 'rooms';
  return {
    id: key ? key.id : `role:${role}`,
    role,
    key,
    label: fixed ? (ROLE_LABEL[role] ?? key?.label ?? role) : key.label,
    ref: withRef ? (LEGAL_REF[role] ?? null) : null,
  };
}

/**
 * The catalogue keys a view covers, in template (or catalogue) order, minus
 * the header keys. Empty for a template the project no longer has.
 */
export function viewKeys(
  view: FieldView,
  templates: readonly Template[],
  catalogue: readonly ResourceKey[],
): ResourceKey[] {
  if (view.kind === 'all') return catalogue.filter((k) => !HEADER_KEYS.has(k.id));
  const template = viewTemplate(view, templates);
  if (!template) return [];
  const byId = new Map(catalogue.map((k) => [k.id, k]));
  const seen = new Set<string>();
  const out: ResourceKey[] = [];
  for (const id of template.resolved_keys) {
    const key = byId.get(id);
    if (!key || seen.has(id) || HEADER_KEYS.has(id)) continue;
    seen.add(id);
    out.push(key);
  }
  return out;
}

/** The catalogue's key for a role (the first by catalogue order), or null. */
export function keyForRole(role: FieldRole, catalogue: readonly ResourceKey[]): ResourceKey | null {
  return catalogue.find((k) => !HEADER_KEYS.has(k.id) && roleOf(k) === role) ?? null;
}

export interface FieldGroupOptions {
  /** Stk types have no Opbygning row (a build-up is per m²/m³). */
  unit?: string;
}

/** `keys` with each category's keys together, categories in order of first appearance (stable). */
function byCategory(keys: readonly ResourceKey[]): ResourceKey[] {
  const runs = new Map<string, ResourceKey[]>();
  for (const key of keys) {
    const list = runs.get(key.category);
    if (list) list.push(key);
    else runs.set(key.category, [key]);
  }
  return [...runs.values()].flat();
}

/**
 * The inspector's groups for a view: its keys in order (Alle felter: by
 * category, with refs), a heading per run of one category, then the report
 * rows the view leaves out (`REPORT_ROLES`) — Fordeling på rum after Mængde
 * and Opbygning after EAK when those rows are there, the rest under "Til
 * rapporten". A left-out row uses the catalogue's key for its role, else it
 * is synthetic. One row per role except `generic` (the first wins); a stk
 * type has no Opbygning row.
 */
export function fieldGroups(
  view: FieldView,
  templates: readonly Template[],
  catalogue: readonly ResourceKey[],
  options: FieldGroupOptions = {},
): FieldGroupSpec[] {
  const stk = options.unit === 'stk';
  const allowed = (role: FieldRole) => !(stk && role === 'buildup');
  const keys = viewKeys(view, templates, catalogue);
  return categoryGroups(view.kind === 'all' ? byCategory(keys) : keys, catalogue, allowed, view.kind === 'all');
}

function categoryGroups(
  keys: readonly ResourceKey[],
  catalogue: readonly ResourceKey[],
  allowed: (role: FieldRole) => boolean,
  withRef: boolean,
): FieldGroupSpec[] {
  const out: FieldGroupSpec[] = [];
  const taken = new Set<FieldRole>();
  let category: string | null = null;
  for (const key of keys) {
    const role = roleOf(key);
    if (!allowed(role)) continue;
    if (role !== 'generic') {
      if (taken.has(role)) continue;
      taken.add(role);
    }
    const row = spec(role, key, withRef);
    if (out.length > 0 && key.category === category) {
      out[out.length - 1].fields.push(row);
    } else {
      // A new run: a category the template comes back to gets a heading again.
      out.push({ id: `cat:${key.category}:${out.length}`, title: key.category, fields: [row] });
      category = key.category;
    }
  }
  const missing = (role: FieldRole) => !taken.has(role) && allowed(role);
  const insertAfter = (anchor: FieldRole, role: FieldRole) => {
    if (!missing(role)) return;
    for (const g of out) {
      const at = g.fields.findIndex((f) => f.role === anchor);
      if (at >= 0) {
        g.fields.splice(at + 1, 0, spec(role, keyForRole(role, catalogue), withRef));
        taken.add(role);
        return;
      }
    }
  };
  insertAfter('quantity', 'rooms');
  insertAfter('eak', 'buildup');
  // Whatever else the report needs and the view leaves out, last.
  const rest = REPORT_ROLES.filter(missing).map((role) => spec(role, keyForRole(role, catalogue), withRef));
  if (rest.length) out.push({ id: 'report', title: REPORT_GROUP_TITLE, fields: rest });
  return out;
}

/**
 * Tabel › Typer's columns for a view: the template's columns (Alle felter:
 * every catalogue key but the fixed Type column), then a column for each
 * report row the view leaves out, in `REPORT_ROLES` order — except Tons (the
 * table's own Masse column) and a role the catalogue has no key for.
 */
export function viewColumns(
  view: FieldView,
  templates: readonly Template[],
  catalogue: readonly ResourceKey[],
): ResourceColumn[] {
  const column = (key: ResourceKey): ResourceColumn => ({ key, typeScoped: key.scope === 'type' });
  const out =
    view.kind === 'all'
      ? catalogue.filter((k) => !FIXED_COLUMN_KEYS.has(k.id)).map(column)
      : templateColumns(viewTemplate(view, templates), catalogue);
  const ids = new Set(out.map((c) => c.key.id));
  const roles = new Set(out.map((c) => roleOf(c.key)));
  for (const role of REPORT_ROLES) {
    if (role === 'tons' || roles.has(role)) continue;
    const key = keyForRole(role, catalogue);
    if (!key || ids.has(key.id)) continue;
    ids.add(key.id);
    out.push(column(key));
  }
  return out;
}

// ---------------------------------------------------------- values, scope --

/** The resource codes a type-level write of `key` goes to: the carrier (type scope) or every part. */
export function writeTargets(type: SurveyType, key: Pick<ResourceKey, 'scope'>): string[] {
  if (key.scope === 'type') {
    const carrier = typeCarrier(type);
    return carrier === null ? [] : [carrier];
  }
  return [...type.parts].map((p) => p.code).sort((a, b) => a.localeCompare(b));
}

export interface TypeValue {
  /** The value shown: the carrier's (type scope), or the parts' most common non-blank value. */
  value: string | null;
  /** The parts disagree (part scope only). */
  mixed: boolean;
}

function blank(v: string | null | undefined): boolean {
  return v === null || v === undefined || v.trim() === '';
}

/** A key's value for a whole type (see `TypeValue`). */
export function typeValue(type: SurveyType, key: Pick<ResourceKey, 'id' | 'scope'>, index: ResourceIndex): TypeValue {
  const codes = writeTargets(type, key);
  if (codes.length === 0) return { value: null, mixed: false };
  const values = codes.map((c) => index.get(c)?.values[key.id] ?? null);
  if (key.scope === 'type') return { value: values[0], mixed: false };
  const counts = new Map<string, number>();
  for (const v of values) if (!blank(v)) counts.set(v as string, (counts.get(v as string) ?? 0) + 1);
  const distinct = new Set(values.map((v) => (blank(v) ? '' : v)));
  let best: string | null = null;
  let bestN = 0;
  for (const [v, n] of counts) {
    if (n > bestN) {
      best = v;
      bestN = n;
    }
  }
  return { value: best, mixed: distinct.size > 1 };
}

/** A type's value for a key read straight from a resource list (no index). */
export function typeValueIn(type: SurveyType, key: Pick<ResourceKey, 'id' | 'scope'>, resources: readonly Resource[]): TypeValue {
  return typeValue(type, key, new Map(resources.map((r) => [r.code, r])));
}

// ------------------------------------------------------------- controls --

/** A multiselect's stored JSON array as options (a malformed value is no options). */
export function multiValues(value: string | null): string[] {
  if (blank(value)) return [];
  try {
    const parsed: unknown = JSON.parse(value as string);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

/** The wire value after toggling `option` in a multiselect; null once nothing is left. */
export function multiToggle(value: string | null, option: string, order: readonly string[] = []): string | null {
  const now = multiValues(value);
  const next = now.includes(option) ? now.filter((v) => v !== option) : [...now, option];
  if (next.length === 0) return null;
  const rank = (v: string) => {
    const i = order.indexOf(v);
    return i < 0 ? order.length : i;
  };
  next.sort((a, b) => rank(a) - rank(b));
  return JSON.stringify(next);
}

/** A boolean's stored value as Ja/Nej/blank. */
export function yesNo(value: string | null): boolean | null {
  if (blank(value)) return null;
  const v = (value as string).trim().toLowerCase();
  if (v === 'true' || v === '1' || v === 'ja' || v === 'yes') return true;
  if (v === 'false' || v === '0' || v === 'nej' || v === 'no') return false;
  return null;
}

/** The wire value for a Ja/Nej press: pressing the shown answer again clears it (when the key may be blank). */
export function yesNoCommit(current: boolean | null, pressed: boolean, clearable = true): string | null | undefined {
  if (current === pressed) return clearable ? null : undefined;
  return pressed ? 'true' : 'false';
}

/**
 * A Ja/Nej press judged against what the control shows: the pending choice
 * while its write is in flight, else the stored wire value. A quick second
 * press therefore clears (or flips) rather than resending the first press's
 * value (b48394a, the table's `choiceCommit` rule).
 */
export function yesNoPress(
  stored: string | null,
  pending: PendingChoice | undefined,
  pressed: boolean,
  clearable = true,
): string | null | undefined {
  return yesNoCommit(yesNo(shownChoice(stored, pending)), pressed, clearable);
}

/** A tag toggle press, judged against the pending choice like {@link yesNoPress}. */
export function tagPress(
  stored: string | null,
  pending: PendingChoice | undefined,
  option: string,
  order: readonly string[] = [],
): string | null {
  return multiToggle(shownChoice(stored, pending), option, order);
}

/** EAK codes the inspector offers (the BEK 496 registreringsark's common fractions). */
export const EAK_OPTIONS: readonly { code: string; name: string }[] = [
  { code: '17.01.01', name: 'Beton' },
  { code: '17.01.02', name: 'Mursten' },
  { code: '17.01.03', name: 'Tegl og keramik' },
  { code: '17.01.07', name: 'Blandinger af beton, mursten og tegl' },
  { code: '17.02.01', name: 'Træ' },
  { code: '17.02.02', name: 'Glas' },
  { code: '17.02.03', name: 'Plast' },
  { code: '17.03.02', name: 'Bitumenblandinger' },
  { code: '17.04.01', name: 'Kobber, bronze, messing' },
  { code: '17.04.02', name: 'Aluminium' },
  { code: '17.04.05', name: 'Jern og stål' },
  { code: '17.06.04', name: 'Isoleringsmaterialer' },
  { code: '17.08.02', name: 'Gipsbaserede byggematerialer' },
  { code: '17.09.04', name: 'Blandet bygge- og nedrivningsaffald' },
  { code: '17.06.05*', name: 'Asbestholdige byggematerialer' },
  { code: '17.09.02*', name: 'PCB-holdigt bygge- og nedrivningsaffald' },
  { code: '17.09.03*', name: 'Andet med farlige stoffer' },
];

/** The EAK choices for a select: the list, plus the type's own code when the list lacks it. */
export function eakChoices(current: string): { code: string; label: string }[] {
  const list = EAK_OPTIONS.map((e) => ({ code: e.code, label: `${e.code} ${e.name}` }));
  const c = current.trim();
  if (c && !list.some((e) => e.code === c)) list.push({ code: c, label: c });
  return list;
}

/** A hazardous-waste EAK code carries a `*`. */
export function isHazardousEak(code: string): boolean {
  return code.includes('*');
}
